import type { ClassifyInput, Verdict } from './types';

export const NOTICE_STALE = 'Recorded (an earlier time was kept)';
export const NOTICE_CONFLICT = 'Recorded; the office will review';
export const DROP_FALLBACK = "Couldn't send";

const BACKOFF_MS = [1000, 2000, 5000];
/** A 408 or 425 repeats the same request: after this many tries (about ten minutes) it fails visibly instead of retrying for days. */
export const MAX_TIMEOUT_ATTEMPTS = 20;

/** The wait before the next attempt, `attempts` counting the one that just failed: 1 s, 2 s, 5 s, then every 30 s. */
export function retryDelayMs(attempts: number): number {
  return BACKOFF_MS[attempts - 1] ?? 30_000;
}

/** The default policy for an answer. Handler verdicts beat it, except a key reuse, which the engine always drops. */
export function classify(res: ClassifyInput, attempts: number): Verdict {
  const { status, code } = res;
  if (res.network || status === undefined) return { kind: 'retry', delayMs: retryDelayMs(attempts) };
  if (status >= 200 && status < 300) {
    if (res.flag === 'conflict') return { kind: 'done', notice: NOTICE_CONFLICT };
    if (res.applied === false || res.flag === 'stale') return { kind: 'done', notice: NOTICE_STALE };
    return { kind: 'done' };
  }
  if (status === 409 && code === 'request_in_progress') return { kind: 'wait', delayMs: 2000 };
  // 408 (a proxy gave up on a slow upload) and 425 (too early) are conditions of the moment, not of the request
  if ((status === 408 || status === 425) && attempts >= MAX_TIMEOUT_ATTEMPTS) return { kind: 'drop', message: res.message || DROP_FALLBACK };
  if (status >= 500 || status === 408 || status === 425 || status === 429 || status < 200) return { kind: 'retry', delayMs: retryDelayMs(attempts) };
  if (status === 401) return { kind: 'hold_auth' };
  if (status === 403 && code === 'subscription_required') return { kind: 'hold_read_only' };
  // 409 visit_cancelled, 422 idempotency_key_reuse, and every other 4xx and 3xx
  return { kind: 'drop', message: res.message || DROP_FALLBACK };
}

export const isKeyReuse = (res: ClassifyInput) => res.status === 422 && res.code === 'idempotency_key_reuse';
